import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { devSubmitSurvey } from '../../features/dev/api';
import {
  Bell,
  Check,
  Globe,
  LogOut,
  Menu,
  NavArrowDown,
  Settings,
  SidebarExpand,
  User,
} from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import i18n, { LANGUAGE_LABEL_KEYS, LANGUAGE_STORAGE_KEY, SUPPORTED_LANGUAGES, isSupportedLanguage } from '../../i18n';
import type { SupportedLanguage } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { useGetProfile } from '../../features/settings/hooks';

export interface QuestionnaireTopHeaderProps {
  title: string;
  /** Mobile only: opens the navigation drawer. */
  onSidebarToggle: () => void;
  /** Current flow session id — used for best-effort server-side language persistence. */
  sessionId?: string | null;
  /** Called after language is changed so parent can react (e.g. re-sync session). */
  onLanguageChange?: (lang: SupportedLanguage) => void;
  /** Dev-tools only: show a button that submits dummy survey data for the current session. */
  showDevSubmit?: boolean;
}

/**
 * Shell header: mobile nav control + section title (left), utility placeholders (right).
 * Sidebar expand/collapse on tablet and desktop lives in `QuestionnaireNavSidebar`.
 * Right-side controls are static (no handlers) until wired to real auth/i18n.
 * On small screens, utilities are grouped under a menu (Menu icon).
 */
export function QuestionnaireTopHeader({
  title,
  onSidebarToggle,
  sessionId,
  onLanguageChange,
  showDevSubmit = false,
}: QuestionnaireTopHeaderProps) {
  const { t, i18n: i18nInstance } = useTranslation('common');
  const currentLang = (isSupportedLanguage(i18nInstance.language)
    ? i18nInstance.language
    : 'en') as SupportedLanguage;

  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const { data: profile } = useGetProfile({ enabled: isAuthenticated });
  const displayName = profile?.full_name ?? null;

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [devSubmitState, setDevSubmitState] = useState<'idle' | 'loading' | 'ok' | 'err'>('idle');
  const [devSubmitError, setDevSubmitError] = useState<string | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = useCallback(async () => {
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
    await logout();
    navigate('/signin');
  }, [logout, navigate]);

  const handleLanguageSelect = useCallback(
    async (lang: SupportedLanguage) => {
      if (lang === currentLang) return;
      await i18n.changeLanguage(lang);
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      if (sessionId) {
        // Best-effort: persist language on the server; do not block the UI.
        try {
          const { flowSetLanguage } = await import('../../features/flow/api');
          void flowSetLanguage(sessionId, lang);
        } catch {
          // Intentionally swallowed — server-side language is optional.
        }
      }
      onLanguageChange?.(lang);
      setLangMenuOpen(false);
      setMobileMenuOpen(false);
    },
    [currentLang, sessionId, onLanguageChange]
  );

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);
  const closeLangMenu = useCallback(() => setLangMenuOpen(false), []);
  const closeUserMenu = useCallback(() => setUserMenuOpen(false), []);

  const handleDevSubmit = useCallback(async () => {
    if (!sessionId || devSubmitState === 'loading') return;
    setDevSubmitState('loading');
    setDevSubmitError(null);
    try {
      const result = await devSubmitSurvey(sessionId);
      console.info('[DEV] submit-survey success', result);
      setDevSubmitState('ok');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error('[DEV] submit-survey failed', { sessionId, error: e });
      setDevSubmitError(msg);
      setDevSubmitState('err');
    }
  }, [sessionId, devSubmitState]);

  useEffect(() => {
    if (!mobileMenuOpen && !langMenuOpen && !userMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMobileMenu();
        closeLangMenu();
        closeUserMenu();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileMenuOpen, langMenuOpen, userMenuOpen, closeMobileMenu, closeLangMenu, closeUserMenu]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const el = mobileMenuRef.current;
      if (el && !el.contains(e.target as Node)) closeMobileMenu();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [mobileMenuOpen, closeMobileMenu]);

  useEffect(() => {
    if (!langMenuOpen) return;
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const el = langMenuRef.current;
      if (el && !el.contains(e.target as Node)) closeLangMenu();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [langMenuOpen, closeLangMenu]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const el = userMenuRef.current;
      if (el && !el.contains(e.target as Node)) closeUserMenu();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [userMenuOpen, closeUserMenu]);

  return (
    <header className="shrink-0 z-10 h-14 min-h-[56px] max-h-14 px-4 md:px-8 flex items-center justify-between gap-4 border-b border-gray-200 bg-[#F9FAFB] md:bg-white md:h-[64px] md:min-h-[64px] md:max-h-[64px]">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex items-center gap-3 md:hidden">
          <button
            type="button"
            onClick={onSidebarToggle}
            className="-ml-2 shrink-0 rounded-lg p-2 text-[#131315] hover:bg-gray-100"
            aria-label="Open navigation"
          >
            <SidebarExpand className="h-6 w-6" />
          </button>
          <div className="h-6 w-px shrink-0 bg-[#E2E8F0]" aria-hidden />
        </div>
        <h1 className="min-w-0 truncate text-lg font-semibold text-[#131315]">{title}</h1>
      </div>

      <div ref={mobileMenuRef} className="relative z-[60] shrink-0 md:hidden">
        <button
          type="button"
          onClick={() => setMobileMenuOpen((o) => !o)}
          className="shrink-0 border-0 bg-transparent p-2 text-[#131315] shadow-none outline-none hover:bg-transparent focus-visible:outline-none active:bg-transparent"
          aria-label={t('header.open_menu')}
          aria-expanded={mobileMenuOpen}
          aria-haspopup="menu"
        >
          <Menu className="h-6 w-6" strokeWidth={2} />
        </button>

        {mobileMenuOpen ? (
          <div
            role="menu"
            aria-label={t('header.account_settings')}
            className="absolute right-0 top-full mt-2 min-w-[min(100vw-2rem,16rem)] max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
          >
            <div className="flex w-full items-center gap-3 px-3 py-2.5 text-[#131315]">
              <Globe className="h-5 w-5 shrink-0" />
              <span className="min-w-0 flex-1 text-sm font-semibold">{t('language.label')}</span>
              <span className="text-xs font-medium text-[#525353]">{currentLang.toUpperCase()}</span>
            </div>
            <div className="border-t border-gray-100 mx-3" />
            {(SUPPORTED_LANGUAGES as readonly SupportedLanguage[]).map((lang) => (
              <button
                key={lang}
                type="button"
                role="menuitem"
                onClick={() => void handleLanguageSelect(lang)}
                className={[
                  'flex w-full items-center gap-3 px-6 py-2 text-left text-sm hover:bg-gray-50',
                  currentLang === lang ? 'font-semibold text-[#131315]' : 'font-normal text-[#525353]',
                ].join(' ')}
              >
                {t(LANGUAGE_LABEL_KEYS[lang])}
                {currentLang === lang ? <Check className="h-4 w-4 shrink-0 ml-auto" /> : null}
              </button>
            ))}
            {showDevSubmit && sessionId ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => { void handleDevSubmit(); closeMobileMenu(); }}
                disabled={devSubmitState === 'loading'}
                className={[
                  'flex w-full flex-col gap-0.5 px-3 py-2.5 text-left disabled:opacity-50',
                  devSubmitState === 'err' ? 'hover:bg-red-50' : 'hover:bg-amber-50',
                ].join(' ')}
              >
                <span className={[
                  'text-sm font-semibold',
                  devSubmitState === 'err' ? 'text-red-700' : 'text-amber-700',
                ].join(' ')}>
                  {devSubmitState === 'loading'
                    ? 'Submitting…'
                    : devSubmitState === 'ok'
                    ? 'Submitted ✓'
                    : devSubmitState === 'err'
                    ? 'Submit Survey [DEV] — Error'
                    : 'Submit Survey [DEV]'}
                </span>
                {devSubmitState === 'err' && devSubmitError ? (
                  <span className="text-xs text-red-600 break-all">{devSubmitError}</span>
                ) : null}
              </button>
            ) : null}
            <button
              type="button"
              role="menuitem"
              onClick={closeMobileMenu}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-[#131315] hover:bg-gray-50"
            >
              <span className="relative shrink-0">
                <Bell className="h-5 w-5" />
                <span className="pointer-events-none absolute right-0 top-0 h-2 w-2 rounded-full bg-[#1066CB]" />
              </span>
              <span className="text-sm font-semibold">{t('header.notifications')}</span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => { navigate('/settings'); closeMobileMenu(); }}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-[#131315] hover:bg-gray-50"
            >
              <Settings className="h-5 w-5 shrink-0" />
              <span className="text-sm font-semibold">{t('header.settings')}</span>
            </button>
            <div className="flex w-full min-w-0 items-center gap-3 px-3 py-2.5 text-[#131315]">
              <User className="h-5 w-5 shrink-0" />
              <span className="min-w-0 truncate text-sm font-semibold">
                {displayName ?? t('header.user_name')}
              </span>
            </div>
            {isAuthenticated ? (
              <>
                <div className="border-t border-gray-100 mx-3" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => void handleLogout()}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-5 w-5 shrink-0" />
                  Sign out
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="hidden shrink-0 items-center gap-0.5 sm:gap-1 md:flex md:gap-2">
        {showDevSubmit && sessionId ? (
          <button
            type="button"
            onClick={() => void handleDevSubmit()}
            disabled={devSubmitState === 'loading'}
            title={devSubmitState === 'err' && devSubmitError ? devSubmitError : undefined}
            className={[
              'shrink-0 rounded-lg border px-3 py-1.5 text-xs font-semibold disabled:opacity-50',
              devSubmitState === 'err'
                ? 'border-red-400 bg-red-50 text-red-700 hover:bg-red-100'
                : 'border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100',
            ].join(' ')}
          >
            {devSubmitState === 'loading'
              ? 'Submitting…'
              : devSubmitState === 'ok'
              ? 'Submitted ✓'
              : devSubmitState === 'err'
              ? 'Error ✗ (hover)'
              : 'Submit Survey [DEV]'}
          </button>
        ) : null}
        <div ref={langMenuRef} className="relative">
          <button
            type="button"
            onClick={() => setLangMenuOpen((o) => !o)}
            className="flex items-center gap-1.5 rounded-lg px-2 py-2 text-[#131315] hover:bg-gray-100"
            aria-label={t('language.label')}
            aria-expanded={langMenuOpen}
            aria-haspopup="listbox"
          >
            <Globe className="h-5 w-5 shrink-0" />
            <span className="text-sm font-semibold">{currentLang.toUpperCase()}</span>
            <NavArrowDown className="h-4 w-4 shrink-0 opacity-70" />
          </button>

          {langMenuOpen ? (
            <div
              role="listbox"
              aria-label={t('header.select_language')}
              className="absolute right-0 top-full mt-1 min-w-[8rem] rounded-xl border border-gray-200 bg-white py-1 shadow-lg z-50"
            >
              {(SUPPORTED_LANGUAGES as readonly SupportedLanguage[]).map((lang) => (
                <button
                  key={lang}
                  type="button"
                  role="option"
                  aria-selected={lang === currentLang}
                  onClick={() => void handleLanguageSelect(lang)}
                  className={[
                    'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50',
                    lang === currentLang
                      ? 'font-semibold text-[#131315]'
                      : 'font-normal text-[#525353]',
                  ].join(' ')}
                >
                  <span>{t(LANGUAGE_LABEL_KEYS[lang])}</span>
                  {lang === currentLang ? (
                    <Check className="h-4 w-4 shrink-0" />
                  ) : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className="relative shrink-0 rounded-lg p-2 text-[#131315] hover:bg-gray-100"
          aria-label={t('header.notifications')}
        >
          <Bell className="h-5 w-5" />
          <span className="pointer-events-none absolute right-1 top-1 h-2 w-2 rounded-full bg-[#1066CB]" />
        </button>
        <button
          type="button"
          className="shrink-0 rounded-lg p-2 text-[#131315] hover:bg-gray-100"
          aria-label={t('header.settings')}
          onClick={() => navigate('/settings')}
        >
          <Settings className="h-5 w-5" />
        </button>
        {isAuthenticated ? (
          <div ref={userMenuRef} className="relative">
            <button
              type="button"
              onClick={() => setUserMenuOpen((o) => !o)}
              className="flex min-w-0 max-w-[120px] items-center gap-2 rounded-lg px-2 py-2 text-[#131315] hover:bg-gray-100 sm:max-w-none"
              aria-label={t('header.user_name')}
              aria-expanded={userMenuOpen}
              aria-haspopup="menu"
            >
              <User className="h-5 w-5 shrink-0" />
              <span className="min-w-0 truncate text-sm font-semibold">
                {displayName ?? t('header.user_name')}
              </span>
              <NavArrowDown className="h-4 w-4 shrink-0 opacity-70" />
            </button>

            {userMenuOpen ? (
              <div
                role="menu"
                className="absolute right-0 top-full z-50 mt-1 min-w-[160px] rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
              >
                {displayName ? (
                  <div className="border-b border-gray-100 px-3 py-2">
                    <p className="truncate text-xs text-gray-400">Signed in as</p>
                    <p className="truncate text-sm font-semibold text-[#131315]">{displayName}</p>
                  </div>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => void handleLogout()}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4 shrink-0" />
                  Sign out
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </header>
  );
}
